import { supabase } from './supabaseClient';
import { PilotStats } from '../types';
import type { PilotStatistics, PilotStatisticsInsert } from '../types/database';
import { calculatePilotStats, getAllPilotNames } from './calculations';
import { loadData } from './storage';

// =====================================================
// PILOT STATISTICS QUERIES
// =====================================================

export const getPilotStatisticsByPilotId = async (
  pilotId: string
): Promise<PilotStatistics | null> => {
  const { data, error } = await supabase
    .from('pilot_statistics')
    .select('*')
    .eq('pilot_id', pilotId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

export const getAllPilotStatistics = async (): Promise<PilotStatistics[]> => {
  const { data, error } = await supabase
    .from('pilot_statistics')
    .select('*')
    .order('last_calculated_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const upsertPilotStatistics = async (
  pilotId: string,
  stats: PilotStats
): Promise<PilotStatistics> => {
  const statisticsData: PilotStatisticsInsert = {
    pilot_id: pilotId,
    flight_time_7d: stats.flightTime7Days,
    flight_time_28d: stats.flightTime28Days,
    flight_time_365d: stats.flightTime365Days,
    duty_time_7d: stats.dutyTime7Days,
    duty_time_28d: stats.dutyTime28Days,
    duty_time_365d: stats.dutyTime365Days,
    sectors_7d: stats.sectors7Days,
    sectors_28d: stats.sectors28Days,
    sectors_365d: stats.sectors365Days,
    sectors_24h: 0, // Will calculate separately if needed
    exceeds_flight_time_7d: stats.exceedances.flightTime7Days,
    exceeds_flight_time_28d: stats.exceedances.flightTime28Days,
    exceeds_flight_time_365d: stats.exceedances.flightTime365Days,
    exceeds_duty_time_7d: stats.exceedances.dutyTime7Days,
    exceeds_duty_time_28d: stats.exceedances.dutyTime28Days,
    exceeds_duty_time_365d: stats.exceedances.dutyTime365Days,
    exceeds_sectors_24h: stats.exceedances.sectors24Hours,
    last_calculated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('pilot_statistics')
    .upsert(statisticsData as any, { onConflict: 'pilot_id' })
    .select()
    .single();

  if (error) throw error;
  return data as PilotStatistics;
};

export const calculateAndStorePilotStatistics = async (
  pilotName: string
): Promise<PilotStatistics | null> => {
  try {
    // Get pilot ID
    const { data: pilot, error: pilotError } = await supabase
      .from('pilots')
      .select('id')
      .eq('name', pilotName)
      .single();

    if (pilotError || !pilot) {
      console.error('Pilot not found:', pilotName);
      return null;
    }

    // Load all data and calculate statistics
    const data = await loadData();
    const stats = calculatePilotStats(pilotName, data.techlogEntries);

    // Store in database
    return await upsertPilotStatistics((pilot as any).id, stats);
  } catch (error) {
    console.error('Error calculating and storing pilot statistics:', error);
    throw error;
  }
};

export const recalculateAllPilotStatistics = async (): Promise<void> => {
  try {
    const data = await loadData();
    const pilotNames = getAllPilotNames(data.techlogEntries);

    // Calculate and store for each pilot
    for (const pilotName of pilotNames) {
      await calculateAndStorePilotStatistics(pilotName);
    }

    console.log(`Recalculated statistics for ${pilotNames.length} pilots`);
  } catch (error) {
    console.error('Error recalculating all pilot statistics:', error);
    throw error;
  }
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================

export const getPilotStatisticsWithPilotInfo = async () => {
  const { data, error } = await supabase
    .from('pilot_statistics')
    .select(`
      *,
      pilot:pilots(*)
    `)
    .order('last_calculated_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

